import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDemoContext } from '../../contexts/DemoContext';
import { formatDisplayDate } from '../../demo/demoUtils';
import {
  ArrowLeft,
  Building2,
  Shield,
  Calendar,
  DollarSign,
  Hash,
  AlertTriangle,
  XCircle,
  CheckCircle,
  FileCheck,
  Clock
} from 'lucide-react';

export default function DemoPolicyDetailPage() {
  const { policyId } = useParams();
  const { policies } = useDemoContext();

  const policy = policies.find(p => p.id === parseInt(policyId));

  if (!policy) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Policy not found</p>
        <Link to="/demo/policies" className="text-primary-600 hover:underline">
          Back to Policies
        </Link>
      </div>
    );
  }

  const formatCurrency = (value) => {
    if (!value) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusInfo = () => {
    if (policy.status === 'expired') {
      return {
        icon: XCircle,
        label: 'Expired',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        description: `Expired ${Math.abs(policy.days_until_expiration)} days ago`
      };
    }
    if (policy.status === 'pending') {
      return {
        icon: Clock,
        label: 'Pending',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        description: 'Policy is not yet effective'
      };
    }
    if (policy.is_expiring_soon) {
      return {
        icon: AlertTriangle,
        label: 'Expiring Soon',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        description: `${policy.days_until_expiration} days until expiration`
      };
    }
    return {
      icon: FileCheck,
      label: 'Active',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      description: `${policy.days_until_expiration} days remaining`
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link to="/demo/policies" className="flex items-center text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Policies
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {policy.policy_name || policy.insurance_type?.name || 'Insurance Policy'}
              </h1>
              <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                <StatusIcon className="h-4 w-4 mr-1" />
                {statusInfo.label}
              </span>
            </div>
            <p className="text-gray-600">{statusInfo.description}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Policy Details Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-gray-400" />
              Policy Details
            </h2>
            <div className="grid grid-cols-2 gap-6">
              {policy.policy_number && (
                <div>
                  <div className="flex items-center text-gray-500 mb-1">
                    <Hash className="h-4 w-4 mr-1" />
                    <span className="text-sm">Policy Number</span>
                  </div>
                  <p className="font-medium text-gray-900">{policy.policy_number}</p>
                </div>
              )}
              {policy.insurer_name && (
                <div>
                  <div className="flex items-center text-gray-500 mb-1">
                    <Building2 className="h-4 w-4 mr-1" />
                    <span className="text-sm">Insurer</span>
                  </div>
                  <p className="font-medium text-gray-900">{policy.insurer_name}</p>
                </div>
              )}
              <div>
                <div className="flex items-center text-gray-500 mb-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span className="text-sm">Effective Date</span>
                </div>
                <p className="font-medium text-gray-900">
                  {formatDisplayDate(policy.effective_date)}
                </p>
              </div>
              <div>
                <div className="flex items-center text-gray-500 mb-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span className="text-sm">Expiration Date</span>
                </div>
                <p className={`font-medium ${
                  policy.status === 'expired' ? 'text-red-600' :
                  policy.is_expiring_soon ? 'text-yellow-600' :
                  'text-gray-900'
                }`}>
                  {formatDisplayDate(policy.expiration_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Coverage Limits Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-gray-400" />
              Coverage Limits
            </h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">GL Occurrence Limit</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(policy.general_liability_occurrence_limit)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">GL Aggregate Limit</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(policy.general_liability_aggregate_limit)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Workers Comp Limit</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(policy.workers_comp_limit)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Additional Insured Status</p>
                <div className="flex items-center">
                  {policy.additional_insured_status ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-green-600 font-medium">Confirmed</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-gray-500">Not confirmed</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Custom Fields Card */}
          {policy.custom_field_definitions && policy.custom_field_definitions.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">
                Custom Fields
              </h2>
              <div className="grid grid-cols-2 gap-6">
                {policy.custom_field_definitions.map((field) => {
                  const value = policy.custom_field_values?.[field.id];
                  return (
                    <div key={field.id}>
                      <p className="text-sm text-gray-500 mb-1">{field.field_name}</p>
                      <p className="font-medium text-gray-900">
                        {value ? (
                          field.field_type === 'date'
                            ? formatDisplayDate(value)
                            : value
                        ) : (
                          <span className="text-gray-400 italic">Not specified</span>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Vendor Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-gray-400" />
              Vendor
            </h2>
            <Link
              to={`/demo/vendors`}
              className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <p className="font-medium text-gray-900 hover:text-primary-600">
                {policy.vendor_name}
              </p>
              <p className="text-sm text-gray-500 mt-1">View vendor details</p>
            </Link>
          </div>

          {/* Expiration Warning */}
          {(policy.status === 'expired' || policy.is_expiring_soon) && (
            <div className={`card p-6 ${
              policy.status === 'expired' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-start">
                {policy.status === 'expired' ? (
                  <XCircle className="h-6 w-6 text-red-500 mr-3 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-yellow-500 mr-3 flex-shrink-0" />
                )}
                <div>
                  <h3 className={`font-medium ${
                    policy.status === 'expired' ? 'text-red-800' : 'text-yellow-800'
                  }`}>
                    {policy.status === 'expired' ? 'Policy Expired' : 'Policy Expiring Soon'}
                  </h3>
                  <p className={`text-sm mt-1 ${
                    policy.status === 'expired' ? 'text-red-700' : 'text-yellow-700'
                  }`}>
                    {policy.status === 'expired'
                      ? 'This policy has expired. Contact the vendor to obtain an updated certificate of insurance.'
                      : `This policy expires in ${policy.days_until_expiration} days. Consider requesting an updated certificate.`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>In Demo Mode:</strong> You're viewing a sample policy.
          In the full version, you can edit policy details, upload certificates, and receive automated expiration alerts.
        </p>
      </div>
    </div>
  );
}
